import { useState, useEffect } from "react";
import { useTheme } from "../context/ThemeContext";
import { motion } from "framer-motion";
import { toast } from "react-hot-toast";
import axiosInstance from "../utils/axiosConfig";
import { FaSync, FaDownload, FaPlus, FaEdit, FaTrash, FaChartBar, FaList, FaGraduationCap, FaChalkboardTeacher, FaBuilding, FaBookOpen, FaFileAlt, FaCheckSquare, FaSpinner, FaClock, FaUserGraduate, FaSearch, FaEye, FaSave, FaPlayCircle, FaPauseCircle, FaBullhorn } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import ManagementCards from "../components/institute/ManagementCards";
import Loader from "../components/Loader";

const InstituteDashboard = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [selectedExam, setSelectedExam] = useState(null);
  const [examResults, setExamResults] = useState([]);
  /* Commented out tab state
  const [activeTab, setActiveTab] = useState(() => {
    return localStorage.getItem("instituteDashboardTab") || "exams";
  });
  */
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isRefreshingExams, setIsRefreshingExams] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [examToDelete, setExamToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.4 }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: { duration: 0.3 }
    }
  };

  useEffect(() => {
    fetchUploads();
  }, []);

  /* Commented out tab storage
  useEffect(() => {
    localStorage.setItem("instituteDashboardTab", activeTab);
  }, [activeTab]);
  */

  const fetchUploads = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("/api/upload/my-uploads");
      if (response.data) {
        setUploads(response.data);
      }
    } catch (error) {
      console.error("Error fetching uploads:", error);
      toast.error(error.response?.data?.message || "Failed to fetch uploads");
      setError(error.response?.data?.message || "Failed to fetch uploads");
    } finally {
      setLoading(false);
    }
  };

  const refreshExams = async () => {
    try {
      setIsRefreshingExams(true);
      await fetchUploads();
      toast.success('Exams refreshed successfully');
    } catch (error) {
      console.error("Error refreshing exams:", error);
      toast.error("Failed to refresh exams");
    } finally {
      setIsRefreshingExams(false);
    }
  };

  const handleViewResults = async (examId) => {
    try {
      setIsRefreshing(true);
      const response = await axiosInstance.get(`/api/exams/results/${examId}`);
      setExamResults(response.data);

      // Find the exam details to display in the modal
      const exam = uploads.find((upload) => upload._id === examId);
      
      // Get the exam details including passingPercentage if not already included
      if (exam && !exam.passingPercentage) {
        try {
          const examDetailsResponse = await axiosInstance.get(`/api/upload/${examId}`);
          if (examDetailsResponse.data) {
            // Merge the detailed exam data with the basic exam data
            setSelectedExam({
              ...exam,
              passingPercentage: examDetailsResponse.data.passingPercentage || 60
            });
          } else {
            setSelectedExam(exam);
          }
        } catch (error) {
          console.error('Error fetching exam details:', error);
          setSelectedExam(exam);
        }
      } else {
        setSelectedExam(exam);
      }
      
      setShowResultsModal(true);
    } catch (error) {
      console.error('Error fetching results:', error);
      toast.error('Failed to fetch results');
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleResultsRefresh = async () => {
    if (!selectedExam) return;

    try {
      setIsRefreshing(true);
      const response = await axiosInstance.get(`/api/exams/results/${selectedExam._id}`);
      setExamResults(response.data);
      toast.success('Results refreshed');
    } catch (error) {
      console.error('Error refreshing results:', error);
      toast.error('Failed to refresh results');
    } finally {
      setIsRefreshing(false);
    }
  };

  const downloadResultsAsCSV = () => {
    if (!examResults.length) {
      toast.error('No results to download');
      return;
    }

    // Create CSV headers
    const headers = ['Student Name', 'Score (%)', 'Correct Answers', 'Total Questions', 'Time Taken', 'Submission Date'];
    
    // Get the passing percentage from the selected exam (default to 60 if not available)
    const passingPercentage = selectedExam?.passingPercentage || 60;
    
    // Convert results to CSV format with properly formatted date
    const csvData = examResults.map(result => {
      // Format the date properly
      const submissionDate = result.submittedAt 
        ? new Date(result.submittedAt).toLocaleString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
          })
        : 'N/A';
      
      // Format time taken
      const timeTaken = result.timeTaken !== undefined && result.timeTaken !== null
        ? `${Math.floor(result.timeTaken / 60)}m ${result.timeTaken % 60}s`
        : 'N/A';

      // Check if student failed (score below passing percentage)
      const hasFailed = result.score < passingPercentage;
      
      return {
        data: [
          result.student?.name || 'Deleted User',
          result.score?.toFixed(2) || '0.00',
          result.correctAnswers || '0',
          result.totalQuestions || '0',
          timeTaken,
          submissionDate
        ],
        hasFailed
      };
    });

    // For Excel, we'll use a different approach - create an HTML table that Excel can interpret
    let htmlTable = '<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">';
    htmlTable += '<head><!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet><x:Name>Exam Results</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions></x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]--></head>';
    htmlTable += '<body><table>';
    
    // Add header row
    htmlTable += '<tr>';
    headers.forEach(header => {
      htmlTable += `<th style="background-color:#E0E0E0; font-weight:bold;">${header}</th>`;
    });
    htmlTable += '</tr>';
    
    // Add data rows
    csvData.forEach(row => {
      if (row.hasFailed) {
        // Red background for failed students
        htmlTable += '<tr>';
        row.data.forEach(cell => {
          htmlTable += `<td style="background-color:#FFCCCC;">${cell}</td>`;
        });
        htmlTable += '</tr>';
      } else {
        // Normal background for passed students
        htmlTable += '<tr>';
        row.data.forEach(cell => {
          htmlTable += `<td>${cell}</td>`;
        });
        htmlTable += '</tr>';
      }
    });
    
    htmlTable += '</table></body></html>';
    
    // Create blob and download
    const blob = new Blob([htmlTable], { type: 'application/vnd.ms-excel' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    // Create filename with current date
    const currentDate = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).replace(/\//g, '-');
    
    link.setAttribute('href', url);
    link.setAttribute('download', `${selectedExam.examName}_results_${currentDate}.xls`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleReleaseResults = async (examId) => {
    try {
      const response = await axiosInstance.post(`/api/exams/release/${examId}`);

      // Update the uploads state to reflect the results release
      setUploads((prevUploads) =>
        prevUploads.map((upload) =>
          upload._id === examId ? { ...upload, resultsReleased: true } : upload
        )
      );

      toast.success(response.data.message);
      
      // If the modal is open and showing this exam's results, refresh them
      if (selectedExam?._id === examId) {
        const resultsResponse = await axiosInstance.get(`/api/exams/results/${examId}`);
        setExamResults(resultsResponse.data);
      }
    } catch (error) {
      console.error('Error releasing results:', error);
      toast.error('Failed to release results');
    }
  };

  const handleToggleExamMode = async (examId) => {
    const currentExam = uploads.find((upload) => upload._id === examId);

    // Check if the exam is approved
    if (!currentExam || currentExam.status !== "approved") {
      toast.error("Exam mode can only be toggled for approved exams.");
      return;
    }

    try {
      const newExamMode = !currentExam.examMode; // Toggle the current state

      const response = await axiosInstance.put(
        `/api/exams/${examId}/exam-mode`,
        {
          examMode: newExamMode, // Send the new state to the server
        }
      );

      // Update the uploads state to reflect the new exam mode
      setUploads((prevUploads) =>
        prevUploads.map((upload) =>
          upload._id === examId ? { ...upload, examMode: newExamMode } : upload
        )
      );

      toast.success(response.data.message);
    } catch (error) {
      console.error("Error toggling exam mode:", error);
      toast.error("Failed to toggle exam mode");
    }
  };

  const handleManageDepartments = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    navigate('/institute/departments');
  };

  const handleManageFaculty = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    navigate('/institute/faculty');
  };

  const handleManageSubjects = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    navigate('/institute/subjects');
  };

  const handleManagePapers = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    toast.error("Paper management feature is unavailable.");
  };

  const handleDeleteExam = async () => {
    if (!examToDelete) return;
    
    // Ensure the confirmation text matches exactly
    if (deleteConfirmText.toLowerCase() !== 'delete') {
      toast.error('Please type "delete" to confirm');
      return;
    }
    
    try {
      setIsDeleting(true);
      const response = await axiosInstance.delete(`/api/upload/${examToDelete._id}`);
      
      // Remove the deleted exam from state
      setUploads(prevUploads => prevUploads.filter(upload => upload._id !== examToDelete._id));
      
      toast.success(response.data?.message || 'Exam deleted successfully');
      setShowDeleteModal(false);
      setExamToDelete(null);
      setDeleteConfirmText('');
    } catch (error) {
      console.error('Error deleting exam:', error);
      toast.error(error.response?.data?.message || 'Failed to delete exam');
    } finally {
      setIsDeleting(false);
    }
  };

  const confirmDeleteExam = (exam) => {
    setExamToDelete(exam);
    setDeleteConfirmText('');
    setShowDeleteModal(true);
  };

  // Function to filter exams based on search query
  const getFilteredExams = () => {
    if (!searchQuery.trim()) return uploads;
    
    const query = searchQuery.toLowerCase().trim();
    return uploads.filter(exam => 
      exam.examName.toLowerCase().includes(query) || 
      (exam.description && exam.description.toLowerCase().includes(query))
    );
  };

  const renderExamsTab = () => {
    const filteredExams = getFilteredExams();
    
    return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-8"
    >
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <motion.div
          variants={cardVariants}
          className={`p-6 rounded-xl ${
            isDarkMode ? 'bg-gray-800' : 'bg-white'
          } shadow-lg`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total Exams</p>
              <h3 className={`text-2xl font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {uploads.length}
              </h3>
              </div>
            <div className={`p-3 rounded-full ${isDarkMode ? 'bg-violet-900/20 text-violet-400' : 'bg-violet-100 text-violet-600'}`}>
              <FaChartBar className="w-6 h-6" />
            </div>
              </div>
        </motion.div>

        <motion.div
          variants={cardVariants}
          className={`p-6 rounded-xl ${
            isDarkMode ? 'bg-gray-800' : 'bg-white'
          } shadow-lg`}
        >
          <div className="flex items-center justify-between">
              <div>
              <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Active Exams</p>
              <h3 className={`text-2xl font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {uploads.filter(exam => exam.examMode).length}
              </h3>
              </div>
            <div className={`p-3 rounded-full ${isDarkMode ? 'bg-green-900/20 text-green-400' : 'bg-green-100 text-green-600'}`}>
              <FaGraduationCap className="w-6 h-6" />
                  </div>
                </div>
        </motion.div>

        <motion.div
          variants={cardVariants}
          className={`p-6 rounded-xl ${
            isDarkMode ? 'bg-gray-800' : 'bg-white'
          } shadow-lg`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Pending Approval</p>
              <h3 className={`text-2xl font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {uploads.filter(exam => exam.status === 'pending').length}
              </h3>
                </div>
            <div className={`p-3 rounded-full ${isDarkMode ? 'bg-yellow-900/20 text-yellow-400' : 'bg-yellow-100 text-yellow-600'}`}>
              <FaChalkboardTeacher className="w-6 h-6" />
                </div>
              </div>
          </motion.div>
      </div>

      {/* Exams Section */}
      <div className={`rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 px-6 py-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}">
          <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Your Exams
          </h2>
          <div className="flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto">
            {/* Search input */}
            <div className="relative w-full sm:w-64">
              <input
                type="text"
                placeholder="Search exams..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`w-full pl-10 pr-4 py-2 rounded-lg ${
                  isDarkMode 
                    ? 'bg-gray-700 text-white border-gray-600 focus:border-violet-500' 
                    : 'bg-white text-gray-900 border-gray-300 focus:border-violet-500'
                } border focus:outline-none`}
              />
              <FaSearch className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`} />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${
                    isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  ×
                </button>
              )}
            </div>
            <button
              onClick={refreshExams}
              disabled={isRefreshingExams}
              className={`whitespace-nowrap p-2 rounded-lg font-medium inline-flex items-center justify-center transition-all duration-200 ${
                isDarkMode
                  ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
              }`}
              title="Refresh Exams"
            >
              <FaSync className={isRefreshingExams ? 'animate-spin' : ''} />
              <span className="ml-2 text-sm">Refresh</span>
            </button>
          </div>
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {(loading || isRefreshingExams) ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader size="medium" text={isRefreshingExams ? 'Refreshing exams...' : 'Loading exams...'} />
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12">
              <p className={`text-sm text-red-500`}>{error}</p>
              <button 
                onClick={refreshExams}
                className={`mt-4 px-4 py-2 rounded-lg text-sm ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
              >
                Try Again
              </button>
            </div>
          ) : filteredExams.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              {searchQuery ? (
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  No exams found matching "{searchQuery}".
                </p>
              ) : (
                <>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    No exams found. Create your first exam to get started.
                  </p>
                  <button 
                    onClick={() => navigate('/institute/exam/create')}
                    className={`mt-4 px-4 py-2 rounded-lg text-sm bg-violet-600 hover:bg-violet-700 text-white`}
                  >
                    Create Exam
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full table-fixed">
                <thead className="sticky top-0 z-10">
                  <tr className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-b`}>
                    <th className={`px-6 py-5 text-left text-sm font-medium w-1/5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Exam Name
                    </th>
                    <th className={`px-6 py-5 text-left text-sm font-medium w-1/6 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Description
                    </th>
                    <th className={`px-6 py-5 text-left text-sm font-medium w-[8%] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Status
                    </th>
                    <th className={`px-6 py-5 text-left text-sm font-medium w-[8%] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Created
                    </th>
                    <th className={`px-6 py-5 text-left text-sm font-medium w-[12%] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Results
                    </th>
                    <th className={`px-6 py-5 text-left text-sm font-medium w-[12%] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Actions
                    </th>
                    <th className={`px-6 py-5 text-left text-sm font-medium w-[8%] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Mode
                    </th>
                    <th className={`px-6 py-5 text-left text-sm font-medium w-[10%] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Toggle
                    </th>
                    <th className={`px-6 py-5 text-left text-sm font-medium w-[8%] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Delete
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredExams.map((upload, index) => (
                    <tr 
                      key={upload._id} 
                      className={`${
                        isDarkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'
                      } ${
                        index !== filteredExams.length - 1 ? `border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}` : ''
                      }`}
                    >
                      <td className={`px-6 py-4 text-sm font-medium truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            {upload.examName}
                          </td>
                      <td className={`px-6 py-4 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        <div 
                          className="max-w-[200px] overflow-hidden"
                          title={upload.description || 'No description'}
                        >
                          <p className="truncate">
                            {upload.description || 'No description'}
                          </p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          upload.status === 'approved'
                                  ? isDarkMode
                              ? 'bg-green-400/10 text-green-400' 
                              : 'bg-green-100 text-green-700'
                                  : isDarkMode
                              ? 'bg-yellow-400/10 text-yellow-400'
                              : 'bg-yellow-100 text-yellow-700'
                        }`}>
                              {upload.status}
                            </span>
                          </td>
                      <td className={`px-6 py-4 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        {new Date(upload.createdAt).toLocaleDateString('en-GB')}
                          </td>
                      <td className="px-6 py-4">
                            {upload.status === "approved" ? (
                              <button
                                onClick={() => handleViewResults(upload._id)}
                                className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                                  isDarkMode
                                    ? 'text-violet-400 hover:bg-violet-400/10'
                                    : 'text-violet-600 hover:bg-violet-50'
                                }`}
                              >
                                View Results
                              </button>
                            ) : (
                              <span className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                Pending Approval
                              </span>
                            )}
                          </td>
                      <td className="px-6 py-4">
                        {upload.status === "approved" ? (
                          !upload.resultsReleased ? (
                            <button
                              onClick={() => handleReleaseResults(upload._id)}
                              className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                                isDarkMode
                                  ? 'text-green-400 hover:bg-green-400/10'
                                  : 'text-green-600 hover:bg-green-50'
                              }`}
                            >
                              Release Results
                            </button>
                          ) : (
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              isDarkMode ? 'bg-green-400/10 text-green-400' : 'bg-green-100 text-green-700'
                            }`}>
                              Results Released
                            </span>
                          )
                        ) : (
                          <span className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                            Pending Approval
                          </span>
                        )}
                          </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                upload.examMode
                                  ? isDarkMode
                              ? 'bg-green-400/10 text-green-400' 
                              : 'bg-green-100 text-green-700'
                                  : isDarkMode
                              ? 'bg-red-400/10 text-red-400'
                              : 'bg-red-100 text-red-700'
                        }`}>
                          {upload.examMode ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                      <td className="px-6 py-4">
                            <button
                              onClick={() => handleToggleExamMode(upload._id)}
                              disabled={upload.status !== "approved"}
                          className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                            upload.examMode
                                    ? isDarkMode
                                ? 'text-red-400 hover:bg-red-400/10'
                                : 'text-red-600 hover:bg-red-50'
                                    : isDarkMode
                                ? 'text-green-400 hover:bg-green-400/10'
                                : 'text-green-600 hover:bg-green-50'
                          } disabled:opacity-50 disabled:cursor-not-allowed`}
                            >
                              {upload.examMode ? 'Deactivate' : 'Activate'}
                            </button>
                          </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => confirmDeleteExam(upload)}
                          className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                            isDarkMode
                              ? 'text-red-400 hover:bg-red-400/10'
                              : 'text-red-600 hover:bg-red-50'
                          }`}
                        >
                          <FaTrash className="mr-1" /> Delete
                        </button>
                      </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
          )}
        </div>
      </div>
    </motion.div>
    );
  };

  const renderResultsModal = () => {
    if (!showResultsModal || !selectedExam) return null;

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          <div className="fixed inset-0 transition-opacity" aria-hidden="true">
            <div className={`absolute inset-0 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-500'} opacity-75`}></div>
          </div>

          <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

          <div className={`inline-block align-bottom rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full ${
            isDarkMode ? 'bg-gray-800' : 'bg-white'
          }`}>
            {/* Modal Header - Fixed */}
            <div className={`px-4 pt-5 pb-4 sm:px-6 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex justify-between items-center">
                <div>
                  <h3 className={`text-lg leading-6 font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {selectedExam.examName} - Results
                  </h3>
                  <p className={`mt-1 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Exam Results Overview
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleResultsRefresh}
                    className={`p-2 rounded-lg ${
                      isDarkMode
                        ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                    }`}
                  >
                    {isRefreshing ? (
                      <FaSync className="animate-spin" />
                    ) : (
                      <FaSync />
                    )}
                  </button>
                  <button
                    onClick={downloadResultsAsCSV}
                    className={`p-2 rounded-lg ${
                      isDarkMode
                        ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                    }`}
                  >
                    <FaDownload />
                  </button>
                </div>
              </div>
            </div>

            {/* Modal Content - Scrollable */}
            <div className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'} max-h-[60vh] overflow-y-auto p-6`}>
              {examResults.length > 0 ? (
                <div>
                  {/* Results Summary */}
                  <div className={`p-4 rounded-lg mb-4 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <h4 className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      Results Summary
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {(() => {
                        const passingPercentage = selectedExam?.passingPercentage || 60;
                        const totalStudents = examResults.length;
                        const passedStudents = examResults.filter(r => r.score >= passingPercentage).length;
                        const failedStudents = totalStudents - passedStudents;
                        const passRate = totalStudents > 0 ? (passedStudents / totalStudents * 100).toFixed(1) : 0;
                        
                        return (
                          <>
                            <div>
                              <div className="text-sm text-gray-500">Total Students</div>
                              <div className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                {totalStudents}
                              </div>
                            </div>
                            <div>
                              <div className="text-sm text-gray-500">Passed</div>
                              <div className={`text-xl font-semibold text-green-500`}>
                                {passedStudents} ({passRate}%)
                              </div>
                            </div>
                            <div>
                              <div className="text-sm text-gray-500">Failed</div>
                              <div className={`text-xl font-semibold text-red-500`}>
                                {failedStudents} ({(100 - passRate).toFixed(1)}%)
                              </div>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4 mb-3">
                    <div className="text-sm">
                      <span className="font-medium">Passing Score: </span>
                      <span>{selectedExam?.passingPercentage || 60}%</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className={`w-4 h-4 rounded ${isDarkMode ? 'bg-red-900/30' : 'bg-red-100'}`}></div>
                      <span className="text-sm">Failed</span>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className={isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                        <tr>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Student
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Score
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Correct/Total
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Time Taken
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Submitted At
                          </th>
                        </tr>
                      </thead>
                      <tbody className={`divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                        {examResults.map((result) => {
                          // Get the passing percentage (default to 60 if not available)
                          const passingPercentage = selectedExam?.passingPercentage || 60;
                          // Check if student failed
                          const hasFailed = result.score < passingPercentage;
                          
                          return (
                            <tr 
                              key={result._id}
                              className={`${hasFailed ? 
                                (isDarkMode ? 'bg-red-900/30' : 'bg-red-100') : ''
                              }`}
                            >
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                                  {result.student?.name || 'Anonymous'}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {result.student?.email || 'N/A'}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                  result.score >= 70
                                    ? 'bg-green-100 text-green-800'
                                    : result.score >= passingPercentage
                                      ? 'bg-yellow-100 text-yellow-800'
                                      : 'bg-red-100 text-red-800'
                                }`}>
                                  {result.score?.toFixed(1)}%
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                {result.correctAnswers}/{result.totalQuestions}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                {result.timeTaken !== undefined && result.timeTaken !== null
                                  ? `${Math.floor(result.timeTaken / 60)}m ${result.timeTaken % 60}s`
                                  : 'N/A'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                {new Date(result.submittedAt).toLocaleString()}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  No results available for this exam yet.
                </div>
              )}
            </div>

            {/* Modal Footer - Fixed */}
            <div className={`px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse ${
              isDarkMode ? 'bg-gray-800 border-t border-gray-700' : 'bg-gray-50 border-t border-gray-200'
            }`}>
              <button
                type="button"
                onClick={() => setShowResultsModal(false)}
                className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium sm:ml-3 sm:w-auto sm:text-sm ${
                  isDarkMode
                    ? 'bg-gray-700 hover:bg-gray-600 text-white'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                }`}
              >
                Close
              </button>
              {selectedExam && selectedExam.status === "approved" && !selectedExam.resultsReleased && (
                <button
                  type="button"
                  onClick={() => handleReleaseResults(selectedExam._id)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-violet-600 text-base font-medium text-white hover:bg-violet-700 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Release Results
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderDeleteConfirmationModal = () => {
    if (!showDeleteModal || !examToDelete) return null;

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          <div className="fixed inset-0 transition-opacity" aria-hidden="true">
            <div className={`absolute inset-0 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-500'} opacity-75`}></div>
          </div>

          <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

          <div className={`inline-block align-bottom rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full ${
            isDarkMode ? 'bg-gray-800' : 'bg-white'
          }`}>
            <div className="px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div className={`mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full ${
                  isDarkMode ? 'bg-red-900' : 'bg-red-100'
                } sm:mx-0 sm:h-10 sm:w-10`}>
                  <FaTrash className={`${isDarkMode ? 'text-red-400' : 'text-red-600'}`} />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3 className={`text-lg leading-6 font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Delete Exam
                  </h3>
                  <div className="mt-2">
                    <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                      Are you sure you want to delete the exam <span className="font-semibold">{examToDelete.examName}</span>? 
                      This action cannot be undone and all related data will be permanently deleted.
                    </p>
                    
                    <div className="mt-4">
                      <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Type <span className="font-bold">delete</span> to confirm:
                      </label>
                      <input
                        type="text"
                        value={deleteConfirmText}
                        onChange={(e) => setDeleteConfirmText(e.target.value)}
                        className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 ${
                          isDarkMode 
                            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                        }`}
                        placeholder="Type 'delete' to confirm"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className={`px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse ${
              isDarkMode ? 'bg-gray-800 border-t border-gray-700' : 'bg-gray-50 border-t border-gray-200'
            }`}>
              <button
                type="button"
                onClick={handleDeleteExam}
                disabled={isDeleting || deleteConfirmText.toLowerCase() !== 'delete'}
                className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm ${
                  (isDeleting || deleteConfirmText.toLowerCase() !== 'delete') ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowDeleteModal(false);
                  setExamToDelete(null);
                  setDeleteConfirmText('');
                }}
                disabled={isDeleting}
                className={`mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 ${
                  isDarkMode 
                    ? 'bg-gray-700 hover:bg-gray-600 text-gray-300 border-gray-600' 
                    : 'bg-white hover:bg-gray-50 text-gray-700'
                } text-base font-medium focus:outline-none sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm`}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderEnhancedManagementSection = () => (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-8"
    >
      {/* Main management cards grid - 3 columns */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <motion.div
          variants={cardVariants}
          onClick={handleManageDepartments}
          className={`p-8 rounded-xl ${
            isDarkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-white hover:bg-gray-50'
          } shadow-lg cursor-pointer transition-all duration-300 flex flex-col justify-between h-64 border ${
            isDarkMode ? 'border-gray-700' : 'border-gray-200'
          }`}
        >
          <div className={`p-4 rounded-full inline-flex ${isDarkMode ? 'bg-blue-900/20 text-blue-400' : 'bg-blue-100 text-blue-600'} mb-4`}>
            <FaBuilding className="w-8 h-8" />
          </div>
          <div>
            <h3 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Departments
            </h3>
            <p className={`text-base ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mb-4`}>
              Create, manage and organize your institution's departments
            </p>
            <div className={`inline-flex items-center text-sm font-semibold ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
              Manage Departments
              <span className="ml-2">→</span>
            </div>
          </div>
        </motion.div>

        <motion.div
          variants={cardVariants}
          onClick={handleManageFaculty}
          className={`p-8 rounded-xl ${
            isDarkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-white hover:bg-gray-50'
          } shadow-lg cursor-pointer transition-all duration-300 flex flex-col justify-between h-64 border ${
            isDarkMode ? 'border-gray-700' : 'border-gray-200'
          }`}
        >
          <div className={`p-4 rounded-full inline-flex ${isDarkMode ? 'bg-green-900/20 text-green-400' : 'bg-green-100 text-green-600'} mb-4`}>
            <FaChalkboardTeacher className="w-8 h-8" />
          </div>
          <div>
            <h3 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Faculty
            </h3>
            <p className={`text-base ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mb-4`}>
              Manage faculty profiles, assign departments and subjects
            </p>
            <div className={`inline-flex items-center text-sm font-semibold ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
              Manage Faculty
              <span className="ml-2">→</span>
            </div>
          </div>
        </motion.div>

        <motion.div
          variants={cardVariants}
          onClick={handleManageSubjects}
          className={`p-8 rounded-xl ${
            isDarkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-white hover:bg-gray-50'
          } shadow-lg cursor-pointer transition-all duration-300 flex flex-col justify-between h-64 border ${
            isDarkMode ? 'border-gray-700' : 'border-gray-200'
          }`}
        >
          <div className={`p-4 rounded-full inline-flex ${isDarkMode ? 'bg-yellow-900/20 text-yellow-400' : 'bg-yellow-100 text-yellow-600'} mb-4`}>
            <FaBookOpen className="w-8 h-8" />
          </div>
          <div>
            <h3 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Subjects
            </h3>
            <p className={`text-base ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mb-4`}>
              Create and manage subjects, assign to departments
            </p>
            <div className={`inline-flex items-center text-sm font-semibold ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
              Manage Subjects
              <span className="ml-2">→</span>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Secondary management cards - 2 columns */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <motion.div
          variants={cardVariants}
          className={`p-8 rounded-xl ${
            isDarkMode ? 'bg-gray-800' : 'bg-white'
          } shadow-lg border ${
            isDarkMode ? 'border-gray-700' : 'border-gray-200'
          }`}
        >
          <div className="flex items-center justify-between mb-6">
            <div className={`p-4 rounded-full ${isDarkMode ? 'bg-purple-900/20 text-purple-400' : 'bg-purple-100 text-purple-600'}`}>
              <FaGraduationCap className="w-6 h-6" />
            </div>
            <button
              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}
              onClick={() => toast.success("Student management coming soon!")}
            >
              View All
            </button>
          </div>
          <h3 className={`text-xl font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Student Management
          </h3>
          <p className={`text-base ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mb-4`}>
            Track student progress, enrollments, and performance. Create student groups and manage accounts.
          </p>
          <div className="mt-4">
            <div className={`flex items-center justify-between py-3 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <span className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Total Students</span>
              <span className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Coming Soon</span>
            </div>
            <div className={`flex items-center justify-between py-3 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <span className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Active Enrollments</span>
              <span className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Coming Soon</span>
            </div>
          </div>
        </motion.div>

        <motion.div
          variants={cardVariants}
          className={`p-8 rounded-xl ${
            isDarkMode ? 'bg-gray-800' : 'bg-white'
          } shadow-lg border ${
            isDarkMode ? 'border-gray-700' : 'border-gray-200'
          }`}
        >
          <div className="flex items-center justify-between mb-6">
            <div className={`p-4 rounded-full ${isDarkMode ? 'bg-indigo-900/20 text-indigo-400' : 'bg-indigo-100 text-indigo-600'}`}>
              <FaChartBar className="w-6 h-6" />
            </div>
            <button
              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}
              onClick={() => toast.success("Analytics coming soon!")}
            >
              View Reports
            </button>
          </div>
          <h3 className={`text-xl font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Analytics & Reports
          </h3>
          <p className={`text-base ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mb-4`}>
            Get insights into your institution's performance, exam statistics, and student outcomes.
          </p>
          <div className="mt-4">
            <div className="grid grid-cols-2 gap-3">
              <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <p className="text-sm text-gray-500 mb-1">Total Exams</p>
                <p className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {uploads.length}
                </p>
              </div>
              <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <p className="text-sm text-gray-500 mb-1">Active Exams</p>
                <p className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {uploads.filter(exam => exam.examMode).length}
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );

  const renderStudentsTab = () => (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-8"
    >
      <div className="flex flex-col items-center justify-center py-12">
        <p className={`text-lg ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Student management features are coming soon.
        </p>
      </div>
    </motion.div>
  );

  const renderAnalyticsTab = () => (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-8"
    >
      <div className="flex flex-col items-center justify-center py-12">
        <p className={`text-lg ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Analytics features are coming soon.
        </p>
      </div>
    </motion.div>
  );

  return (
    <div className={`min-h-screen pt-24 pb-12 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6">
          <div>
            <h1 className={`text-3xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Institute Dashboard</h1>
            <p className={`text-gray-500 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Manage your institute's resources and monitor performance</p>
          </div>
          <div className="mt-4 lg:mt-0">
            {/* <button
              onClick={() => navigate('/institute/exam/create')}
              className="flex items-center px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors"
            >
              <FaPlus className="mr-2" />
              Create New Exam
            </button> */}
          </div>
        </div>

        {/* Remove the ManagementCards component to avoid duplication */}

        {/* Commented out tab navigation 
        <div className="flex flex-wrap border-b mb-4">
          <button
            onClick={() => setActiveTab('exams')}
            className={`py-3 px-4 font-medium border-b-2 ${
              activeTab === 'exams'
                ? 'border-violet-500 text-violet-600 dark:text-violet-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <div className="flex items-center">
              <FaList className="mr-2" />
              Exams
            </div>
          </button>
          <button
            onClick={() => setActiveTab('students')}
            className={`py-3 px-4 font-medium border-b-2 ${
              activeTab === 'students'
                ? 'border-violet-500 text-violet-600 dark:text-violet-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <div className="flex items-center">
              <FaGraduationCap className="mr-2" />
              Students
            </div>
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`py-3 px-4 font-medium border-b-2 ${
              activeTab === 'analytics'
                ? 'border-violet-500 text-violet-600 dark:text-violet-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <div className="flex items-center">
              <FaChartBar className="mr-2" />
              Analytics
            </div>
          </button>
        </div>
        */}

        {/* Replace conditional tab content with enhanced management section */}
        {renderEnhancedManagementSection()}
        
        {/* Commented out conditional tab content 
        {activeTab === 'exams' && renderExamsTab()}
        {activeTab === 'students' && renderStudentsTab()}
        {activeTab === 'analytics' && renderAnalyticsTab()}
        */}

        {showResultsModal && renderResultsModal()}
        {showDeleteModal && renderDeleteConfirmationModal()}
      </div>
    </div>
  );
};

export default InstituteDashboard;